import React, { useState, useEffect, useContext } from 'react';
import { CONTACT_TABS } from '../../constants/index';
import { useFeature } from '../../../../hooks/useFeatureGating';
import { FEATURES } from '../../../../constant/featureSlugs';
import PremiumUpgradeScreen from '../../../../components/commonComponents/PremiumUpgradeScreen/PremiumUpgradeScreen';
import { 
  AttributeList, 
  ProfileAttributeValueCreate, 
  ProfileAttributeValueUpdate, 
  ProfileAttributeValueDelete,
  ProfileAttributeUpdate,
  ProfileAttributeDelete,
  ProfileAttributeCreate,
  AttributeTypes,
  AttributeScopeCreate,
  AttributeScopeUpdate,
  AttributeScopeDelete
} from '../../api/Api';
import Loader from '../../../../components/commonComponents/Loader/Index';
import { Input, Card, Typography, Button, Space, Popover } from 'antd';
import Dropdown from '../commonComponents/dropdown'
import CustomDelete from '../../../../assets/customSVG/CustomDelete';
import Edit from '../../../../assets/customSVG/Edit';
import Close from '../../../../assets/customSVG/Close';
import Tick from '../../../../assets/customSVG/Tick';
import AddCircle from '../../../../assets/customSVG/AddCircle';
import { Address } from '../commonComponents/location/Address';
import { Province } from '../commonComponents/location/Province';
import { City } from '../commonComponents/location/City';
import { countryListApi, stateListApi, cityListApi } from '../../api/Api';
import { gender } from '../../../../constant/app/crm/crm';
// Custom SVG components imported above
import { useSelector } from 'react-redux';
import NotificationContext from '../../../../providers/NotificationProvider';
import { showSuccessToast, showErrorToast } from '../../../../components/commonComponents/gxAntdToast/toastUtils';
const { Title, Text } = Typography;

const styles = {
  tabsContainer: {
    borderBottom: '1px solid #e0e0e0',
    marginBottom: '1rem',
  },
  tabs: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '-1px',
  },
  tabButton: {
    padding: '0.75rem 1.5rem',
    border: '1px solid #e0e0e0',
    borderBottom: 'none',
    borderRadius: '4px 4px 0 0',
    backgroundColor: 'white',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    color: '#6B7280',
    fontSize: '0.875rem',
    fontWeight: 500,
    '&:hover': {
      color: '#1F2937',
      backgroundColor: '#F9FAFB',
    },
  },
  tabButtonActive: {
    color: '#1F2937',
    borderBottom: '2px solid #25C277',
    fontWeight: 600,
    backgroundColor: '#F9FAFB',
  },
  tabContent: {
    padding: '1.5rem',
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
  },
  scopeCard: {
    marginBottom: '1.5rem',
    borderRadius: '0.5rem',
    border: '1px solid #E5E7EB',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  },
  scopeHeader: {
    backgroundColor: '#F9FAFB',
    borderBottom: '1px solid #E5E7EB',
    padding: '0.75rem 1.25rem',
    borderRadius: '0.5rem 0.5rem 0 0',
  },
  scopeBody: {
    padding: '1.25rem',
  },
  attributeRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.75rem 0',
    borderBottom: '1px solid #F3F4F6',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  attributeLabel: {
    width: '250px',
    color: '#4B5563',
    fontWeight: 500,
    fontSize: '0.875rem',
  },
  attributeValue: {
    flex: 1,
    maxWidth: '500px',
    display: 'flex',
    gap: '8px',
  },
  inputField: {
    flex: 1,
    border: '1px solid #E5E7EB',
    borderRadius: '0.375rem',
    padding: '0.5rem 0.75rem',
    backgroundColor: '#F9FAFB',
    color: '#1F2937',
  },
  emptyState: {
    textAlign: 'center',
    padding: '3rem 1rem',
    color: '#6B7280',
    backgroundColor: '#F9FAFB',
    borderRadius: '0.5rem',
    border: '1px dashed #E5E7EB',
  },
};

const ContactPersonSettings = ({ contactPersonId }) => {
  const [activeTab, setActiveTab] = useState(1);
  const [attributes, setAttributes] = useState({
    scopes: [],
    contact_person_values: []
  });
  
  // List of attributes that should not have edit/delete buttons
  const restrictedAttributes = ['mobile_number', 'country_code', 'email_address', 'age'];
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [validationError, setValidationError] = useState('');
  const [editingAttribute, setEditingAttribute] = useState(null);
  const [editedLabel, setEditedLabel] = useState('');
  const [activeAddAttributeScope, setActiveAddAttributeScope] = useState(null);
  const [attributeTypes, setAttributeTypes] = useState([]);
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);
  const [newAttribute, setNewAttribute] = useState({
    name: '',
    type: '',
    typeDropdownOpen: false,
    scopeId: null
  });

  // State for country, state, and city dropdowns
  const [countryList, setCountryList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState({ name: '', id: null });
  const [selectedState, setSelectedState] = useState({ name: '', id: null });
  const [selectedCity, setSelectedCity] = useState({ name: '', id: null });
  const [popoverVisible, setPopoverVisible] = useState({
    country: false,
    state: false,
    city: false,
    gender: false,
  });

  const [newScope, setNewScope] = useState({
    name: '',
    isAdding: false,
    editingId: null,
    editedName: ''
  });

  const { currentBrand } = useSelector((state) => state.app);
  const { isEnabled: canUseCustomAttributes } = useFeature(FEATURES.CUSTOM_ATTRIBUTES);

  // Validate input based on attribute type
  const validateInput = (value, attributeType) => {
    if (value === '') return true; // Allow empty values
    
    switch (attributeType) {
      case 'number':
        return !isNaN(Number(value));
      case 'date':
        // Simple date validation (YYYY-MM-DD)
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(value)) return false;
        const date = new Date(value);
        return date instanceof Date && !isNaN(date);
      case 'boolean':
        // For boolean, we'll use a checkbox, but just in case
        return ['true', 'false', '0', '1', ''].includes(String(value).toLowerCase());
      case 'string':
      default:
        return true; // No validation for string type
    }
  };
  
  // Get input type based on attribute type
  const getInputType = (attributeType) => {
    switch (attributeType) {
      case 'number':
        return 'number';
      case 'date':
        return 'date';
      case 'boolean':
        return 'checkbox';
      case 'string':
      default:
        return 'text';
    }
  };
  
  // Format value for display based on attribute type
  const formatDisplayValue = (value, attributeType, attributeName) => {
    if (value === null || value === undefined || value === '') return 'No value set';

    if (attributeType === 'boolean') {
      return value === 'true' || value === true ? 'true' : 'false';
    }

    // For gender, show the label
    if (attributeName === 'gender') {
      return value || 'No value set';
    }

    // For location attributes, show the selected value
    if (attributeName === 'country' || attributeName === 'province' || attributeName === 'city') {
      return value || 'No value set';
    }

    return value;
  };

  // NOTIFICATION(Toast)
  const notification = useContext(NotificationContext);
  
  // Handle edit button click
  const handleEditClick = (attribute, value) => {
    setEditingId(attribute.id);
    // For boolean values, convert to string 'true'/'false' for the checkbox
    const formattedValue = attribute.attribute_type === 'boolean' 
      ? (value === true || value === 'true' ? 'true' : 'false')
      : (value || '');
    setEditValue(formattedValue);
    setValidationError('');

    // Only reset location selections when starting to edit a location field
    // and the value is empty or doesn't match the current selection
    if (attribute.name === 'country') {
      if (!value) {
        setSelectedCountry({ name: '', id: null });
        setSelectedState({ name: '', id: null });
        setSelectedCity({ name: '', id: null });
        setStateList([]);
        setCityList([]);
      }
    } else if (attribute.name === 'province') {
      // Keep the country selection when editing province
      if (!value) {
        setSelectedState({ name: '', id: null });
        setSelectedCity({ name: '', id: null });
        setCityList([]);
      }
    } else if (attribute.name === 'city') {
      // Keep the country and state selection when editing city
      if (!value) {
        setSelectedCity({ name: '', id: null });
      }
    }
  };

  // Handle save button click
  const handleSave = async (scopeId, attribute) => {
    if (!currentBrand?.brand_id || !contactPersonId) return;
    
    // Validate input based on attribute type
    if (!validateInput(editValue, attribute.attribute_type)) {
      setValidationError(`Please enter a valid ${attribute.attribute_type} value`);
      return;
    }

    try {
      setLoading(true);
      setValidationError('');
      
      // Format the value before saving
      let valueToSave = editValue;
      if (attribute.attribute_type === 'boolean') {
        // Convert string 'true'/'false' to boolean
        valueToSave = String(editValue).toLowerCase() === 'true' ? 'true' : 'false';
      }

      // Check if we need to create or update
      const existingValue = attributes.contact_person_values.find(
        v => v.contact_person_profile_attribute_id === attribute.id
      );

      let response;
      if (existingValue) {
        // Update existing value
        const updatePayload = {
          id: existingValue.id, // This is the profile_attribute_value ID
          contact_person_id: contactPersonId,
          profile_attribute_id: attribute.id, // This is the ID of the attribute type
          value: editValue,
        };

        response = await ProfileAttributeValueUpdate(currentBrand.brand_id, updatePayload);
      } else {
        // Create new value
        const createPayload = {
          contact_person_id: contactPersonId,
          profile_attribute_id: attribute.id,
          value: editValue,
          brand_id: currentBrand.brand_id
        };
        response = await ProfileAttributeValueCreate(createPayload);
      }

      if (response?.status === 200 || response?.status === 201) {
        // Refresh the attributes after successful update
        await fetchAttributes();
        setEditingId(null);
        setEditValue('');
        showSuccessToast(notification, 'Attribute updated successfully');
      } else {
        showErrorToast(notification, response?.response?.data?.error || 'Failed to update attribute');
      }
    } catch (error) {
      showErrorToast(notification, 'Failed to update attribute');
    } finally {
      setLoading(false);
    }
  };

  // Handle attribute label edit
  const handleEditAttribute = (attribute) => {
    setEditingAttribute(attribute.id);
    setEditedLabel(attribute.label || attribute.name);
  };

  // Handle save attribute label
  const handleSaveAttributeLabel = async (scope, attribute) => {
    if (!currentBrand?.brand_id) return;
    
    try {
      setLoading(true);
      
      // Prepare the payload for the API call
      const payload = {
        id: attribute.id,
        name: editedLabel,
        attribute_type: attribute.attribute_type,
        attribute_scope_id: scope.id
      };

      // Call the API to update the attribute
      const response = await ProfileAttributeUpdate(currentBrand.brand_id, payload);
      
      if (response?.status === 200) {
        // Update the local state with the new label
        setAttributes(prev => ({
          ...prev,
          scopes: prev.scopes.map(scope => ({
            ...scope,
            profile_attributes: scope.profile_attributes.map(attr => 
              attr.id === attribute.id 
                ? { ...attr, label: editedLabel }
                : attr
            )
          }))
        }));

        setEditingAttribute(null);
        showSuccessToast(notification, 'Attribute label updated successfully');
      } else {
        throw new Error('Failed to update attribute');
      }
    } catch (error) {
      console.error('Error updating attribute label:', error);
      showErrorToast(notification, error.response?.data?.error || 'Failed to update attribute label');
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel attribute edit
  const handleCancelAttributeEdit = () => {
    setEditingAttribute(null);
    setEditedLabel('');
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue('');
    setValidationError('');
  };

  // Handle input change with validation
  const handleInputChange = (e) => {
    const { value, type, checked } = e.target;
    
    // Handle checkbox differently
    const newValue = type === 'checkbox' 
      ? (checked ? 'true' : 'false')
      : value;
    
    setEditValue(newValue);
    
    // Clear any previous validation error when user starts typing
    if (validationError) {
      setValidationError('');
    }
  };

  // Handle delete attribute
  const handleDeleteAttribute = async (attribute) => {
    if (!currentBrand?.brand_id) return;

    try {
      setLoading(true);
      
      // Call the API to delete the attribute
      const response = await ProfileAttributeDelete(currentBrand.brand_id, {
        id: attribute.id
      });

      if (response?.status === 200) {
        // Update the local state to remove the deleted attribute
        setAttributes(prev => ({
          ...prev,
          scopes: prev.scopes.map(scope => ({
            ...scope,
            profile_attributes: scope.profile_attributes.filter(attr => 
              attr.id !== attribute.id
            )
          }))
        }));
        
        showSuccessToast(notification, 'Attribute deleted successfully');
      } else {
        throw new Error('Failed to delete attribute');
      }
    } catch (error) {
      console.error('Error deleting attribute:', error);
      showErrorToast(notification, error.response?.data?.error || 'Failed to delete attribute');
    } finally {
      setLoading(false);
    }
  };

  // Handle add attribute button click
  const handleAddAttribute = (scopeId) => {
    setActiveAddAttributeScope(scopeId);
    setNewAttribute({
      name: '',
      type: '',
      typeDropdownOpen: false,
      scopeId: scopeId
    });
  };

  // Handle close add attribute form
  const handleAddAttributeClose = () => {
    setActiveAddAttributeScope(null);
    setNewAttribute({
      name: '',
      type: '',
      typeDropdownOpen: false,
      scopeId: null
    });
  };

  // Handle add scope button click
  const handleAddScope = () => {
    setNewScope({
      ...newScope,
      name: '',
      isAdding: true,
      editingId: null
    });
  };

  // Handle edit scope button click
  const handleEditScope = (scope) => {
    setNewScope({
      ...newScope,
      isAdding: false,
      editingId: scope.id,
      editedName: scope.name
    });
  };

  // Handle cancel add/edit scope
  const handleCancelScope = () => {
    setNewScope({
      ...newScope,
      isAdding: false,
      editingId: null,
      editedName: ''
    });
  };

  // Handle create or update scope
  const handleSaveScope = async () => {
    try {
      const scopeName = newScope.editingId ? newScope.editedName : newScope.name;
      
      if (!scopeName.trim()) {
        showErrorToast(notification, 'Please enter a scope name');
        return;
      }

      if (!currentBrand?.brand_id) {
        showErrorToast(notification, 'Brand ID is missing');
        return;
      }

      const payload = {
        name: scopeName.trim(),
        brand_id: currentBrand.brand_id
      };

      setLoading(true);
      let response;
      
      if (newScope.editingId) {
        // Update existing scope
        response = await AttributeScopeUpdate(currentBrand.brand_id, {
          ...payload,
          id: newScope.editingId
        });
      } else {
        // Create new scope
        response = await AttributeScopeCreate(payload);
      }
      
      if (response?.status === 200) {
        const message = newScope.editingId ? 'Scope updated successfully' : 'Scope created successfully';
        showSuccessToast(notification, message);
        setNewScope({ name: '', isAdding: false, editingId: null, editedName: '' });
        await fetchAttributes(); // Refresh the scopes list
      } else {
        showErrorToast(notification, response?.response?.data?.error || 'Failed to save scope');
      }
    } catch (error) {
      showErrorToast(notification, error.response?.data?.error || error.message || 'Failed to save scope');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle delete scope
  const handleDeleteScope = async (scope) => {
    if (!currentBrand?.brand_id) return;
    
    try {
      setLoading(true);
      
      // Call the API to delete the scope
      const response = await AttributeScopeDelete(currentBrand.brand_id, {
        id: scope.id
      });
      
      if (response?.status === 200) {
        showSuccessToast(notification, 'Scope deleted successfully');
        await fetchAttributes(); // Refresh the scopes list
      } else {
        showErrorToast(notification, response?.response?.data?.error || 'Failed to delete scope');
      }
    } catch (error) {
      showErrorToast(notification, error.response?.data?.error || error.message || 'Failed to delete scope');
    } finally {
      setLoading(false);
    }
  };

  // Handle create new attribute
  const handleCreateAttribute = async () => {
    try {
      if (!newAttribute.name || !newAttribute.type || !newAttribute.scopeId) {
        showErrorToast(notification, 'Please fill in all fields');
        return;
      }

      if (!currentBrand?.brand_id) {
        showErrorToast(notification, 'Brand ID is missing');
        return;
      }

      const payload = {
        brand_id: currentBrand.brand_id,
        name: newAttribute.name,
        attribute_type: newAttribute.type,
        attribute_scope_id: newAttribute.scopeId
      };

      setLoading(true);
      const response = await ProfileAttributeCreate(payload);
      
      if (response?.status === 200) {
        showSuccessToast(notification, 'Attribute created successfully');
        handleAddAttributeClose();
        await fetchAttributes(); // Refresh the attributes list
      } else {
        showErrorToast(notification, response?.response?.data?.error || 'Failed to create attribute');
      }
    } catch (error) {
      showErrorToast(notification, error.response?.data?.error || error.message || 'Failed to create attribute');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete attribute value
  const handleDelete = async (valueId) => {
    if (!currentBrand?.brand_id || !contactPersonId) return;
    
    try {
      setLoading(true);
      const res = await ProfileAttributeValueDelete(currentBrand.brand_id, {
        id: valueId,
        contact_person_id: contactPersonId
      });
      
      if (res?.status === 200) {
        showSuccessToast(notification, 'Attribute value deleted');
        await fetchAttributes();
      } else {
        showErrorToast(notification, res?.response?.data?.error || 'Failed to delete attribute value');
      }

    } catch (error) {
      showErrorToast(notification, error.response?.data?.error || error.message || 'Failed to delete attribute value');
    } finally {
      setLoading(false);
    }
  };

  // Fetch attribute types with useCallback to prevent recreation on every render
  const fetchAttributeTypes = React.useCallback(async () => {
    try {
      setIsLoadingTypes(true);
      const response = await AttributeTypes(currentBrand?.brand_id);
      if (response?.data?.attribute_types) {
        setAttributeTypes(response.data.attribute_types);
      }
    } catch (error) {
      showErrorToast(notification, error.response?.data?.error || error.message || 'Failed to load attribute types');
    } finally {
      setIsLoadingTypes(false);
    }
  }, [notification]);

  const fetchAttributes = React.useCallback(async () => {
    let isMounted = true;
    
    try {
      setLoading(true);
      
      const response = await AttributeList(currentBrand?.brand_id, contactPersonId);

      if (response?.data && isMounted) {
        const { scopes_and_attributes_list, contact_person_values = [] } = response.data;
        
        // Create a map of attribute IDs to their values for quick lookup
        const attributeValuesMap = contact_person_values.reduce((acc, cv) => {
          acc[cv.contact_person_profile_attribute_id] = cv.value;
          return acc;
        }, {});
        
        // Initialize mergedScopes with an empty array if scopes_and_attributes_list is null/undefined
        let mergedScopes = [];
        
        if (Array.isArray(scopes_and_attributes_list)) {
          // Map through scopes and their attributes, adding the corresponding values
          mergedScopes = scopes_and_attributes_list.map(scope => ({
            ...scope,
            profile_attributes: Array.isArray(scope.profile_attributes) 
              ? scope.profile_attributes.map(attr => ({
                  ...attr,
                  value: attributeValuesMap[attr.id] || ''
                }))
              : []
          }));
        }
        
        if (isMounted) {
          setAttributes({
            scopes: mergedScopes,
            contact_person_values: contact_person_values
          });
        }
      }
    } catch (error) {
      showErrorToast(notification, error.response?.data?.error || error.message || 'Failed to fetch profile attributes');
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
    
    // No need to return a cleanup function here
  }, [contactPersonId, currentBrand?.brand_id, notification]);
  
  // Fetch country list
  const fetchCountries = async () => {
    try {
      const res = await countryListApi();
      if (res?.data?.data) {
        setCountryList(res.data.data);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  // Fetch state list based on selected country
  const fetchStates = async (countryId) => {
    if (!countryId) return;
    try {
      const res = await stateListApi(countryId);
      if (res?.data?.data) {
        setStateList(res.data.data);
      }
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  // Fetch city list based on selected state
  const fetchCities = async (stateId) => {
    if (!stateId) return;
    try {
      const res = await cityListApi(stateId);
      if (res?.data?.data) {
        setCityList(res.data.data);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  // Handle location selection
  const handleLocationSelect = (type, name, id) => {
    if (type === 'country') {
      setSelectedCountry({ name, id });
      setSelectedState({ name: '', id: null });
      setSelectedCity({ name: '', id: null });
      setStateList([]);
      setCityList([]);
      if (id) fetchStates(id);
    } else if (type === 'state') {
      setSelectedState({ name, id });
      setSelectedCity({ name: '', id: null });
      setCityList([]);
      if (id) fetchCities(id);
    } else if (type === 'city') {
      setSelectedCity({ name, id });
    }

    // Update the edit value based on the selected location
    if (editingId) {
      setEditValue(name);
    }

    // Close the popover
    setPopoverVisible(prev => ({
      ...prev,
      [type]: false
    }));
  };

  // Load data when component mounts or brand/contact changes
  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      if (currentBrand?.brand_id && contactPersonId && isMounted) {
        try {
          setLoading(true);
          await Promise.all([
            fetchAttributes(),
            fetchAttributeTypes(),
            fetchCountries()
          ]);
        } catch (error) {
          // Only show error toast for initial load, not for background refreshes
          if (isMounted) {
            showErrorToast(notification, error.response?.data?.error || error.message || 'Failed to load data');
          }
        } finally {
          if (isMounted) {
            setLoading(false);
          }
        }
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, [currentBrand?.brand_id, contactPersonId, fetchAttributes, fetchAttributeTypes, notification]);

  return (
    <div className="profile-attributes-container w-full">
      {/* Tabs */}
      <div style={styles.tabsContainer}>
        <div style={styles.tabs}>
          {CONTACT_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                ...styles.tabButton,
                ...(activeTab === tab.id ? styles.tabButtonActive : {}),
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div style={styles.tabContent}>
        {loading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '40px',
            minHeight: '200px'
          }}>
            <Loader 
              Width="32px"
              Height="32px"
              loaderColor="var(--new-primary)"
              loaderBg="var(--BG-300)"
            />
          </div>
        ) : activeTab === 1 && (
          <div className="profile-attributes-content">
            {!canUseCustomAttributes ? (
              <PremiumUpgradeScreen 
                title="Unlock Custom Profile Attributes"
                message="Create and manage custom fields for your contacts to store specific business data. Upgrade to access."
              />
            ) : (
              <>
                {attributes.scopes.length > 0 ? (
                  attributes.scopes.map((scope) => (
                    <Card 
                      key={scope.id} 
                      style={styles.scopeCard}
                    >
                      <div style={styles.scopeHeader} className="flex items-center gap-3">
                        <Title level={5} style={{ margin: 0, color: '#111827' }}>
                          {newScope.editingId === scope.id ? (
                            <Input
                              className="w-64 text-[0.8vw] rounded-lg"
                              value={newScope.editedName}
                              onChange={(e) => setNewScope({...newScope, editedName: e.target.value})}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveScope();
                                if (e.key === 'Escape') handleCancelScope();
                              }}
                              autoFocus
                            />
                          ) : (
                            scope.label || scope.name
                          )}
                        </Title>
                        {!scope.default && (
                          <div className="flex items-center gap-2">
                            {newScope.editingId === scope.id ? (
                              <>
                                <button
                                  onClick={handleSaveScope}
                                  disabled={!newScope.editedName.trim()}
                                >
                                  <Tick width="1.2vw" height="1.2vw" strokeWidth="3.2" />
                                </button>
                                <button
                                  onClick={handleCancelScope}
                                >
                                  <Close stroke='#616874' circle={false} width="2vw" height="2vw" />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleEditScope(scope)}
                                >
                                  <Edit stroke="#616874" width="1.2vw" height="1.2vw" />
                                </button>
                                <button
                                  onClick={() => handleDeleteScope(scope)}
                                >
                                  <CustomDelete color="#F96056" width="2vw" height="2vw" />
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      <div style={styles.scopeBody}>
                        {/* Attribute List */}
                        {scope.profile_attributes
                          .filter(attribute => attribute.name !== 'whatsapp_opt_out')
                          .map((attribute) => (
                            <div key={attribute.id} style={styles.attributeRow}>
                            <div style={styles.attributeLabel}>
                              {editingAttribute === attribute.id ? (
                                <div className="flex items-center gap-2 w-full">
                                  <Input
                                    value={editedLabel}
                                    onChange={(e) => setEditedLabel(e.target.value)}
                                    className="w-[70%] text-[0.8vw] rounded-lg"
                                    size="small"
                                    suffix={
                                      <div className="flex items-center">
                                        <button
                                          onClick={() => handleSaveAttributeLabel(scope, attribute)}
                                          className="p-1 hover:bg-gray-200 rounded"
                                        >
                                          <Tick width="0.8vw" height="0.8vw" strokeWidth="3.2" />
                                        </button>
                                        <button
                                          className="p-1 hover:bg-gray-200 rounded"
                                          onClick={handleCancelAttributeEdit}
                                        >
                                          <Close stroke="#616874" circle={false} width="1.5vw" height="1.5vw" />
                                        </button>
                                      </div>
                                    }
                                  />
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 w-full">
                                    <Text>{attribute.label || attribute.name}</Text>
                                    <div className="flex gap-2">
                                      {!attribute.default && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditAttribute(attribute);
                                          }}
                                      className="text-gray-500 hover:text-gray-700"
                                    >
                                      <Edit stroke="#616874" style={{ width: '14px', height: '14px' }} />
                                    </button>
                                    )}
                                    {!attribute.default && (
                                      <button
                                        onClick={(e) => {
                                          handleDeleteAttribute(attribute);
                                        }}
                                        className="text-red-500 hover:text-red-700"
                                      >
                                        <CustomDelete color="#F96056" width="14px" height="14px" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                            <div style={styles.attributeValue}>
                              <div style={styles.attributeValue}>
                                {editingId === attribute.id ? (
                                  <>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                                      {attribute.attribute_type === 'boolean' ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                          <input
                                            type="checkbox"
                                            checked={editValue === 'true'}
                                            onChange={handleInputChange}
                                            disabled={loading}
                                            style={{ width: '16px', height: '16px' }}
                                          />
                                          <span>{editValue === 'true' ? 'true' : 'false'}</span>
                                        </div>
                                      ) : attribute.name === 'gender' ? (
                                        <Popover
                                          content={
                                            <div className="flex flex-col max-h-60 overflow-y-auto w-[10vw]">
                                              {gender.map((item) => (
                                                <div
                                                  key={item.id}
                                                  className="p-2 hover:bg-gray-100 cursor-pointer flex justify-between items-center"
                                                  onClick={() => {
                                                    setEditValue(item.label);
                                                    setPopoverVisible(prev => ({ ...prev, gender: false }));
                                                  }}
                                                >
                                                  <span>{item.label}</span>
                                                  {editValue === item.label && (
                                                    <Tick width="12px" height="12px" strokeWidth="3.2" className="text-green-500" />
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          }
                                          trigger="click"
                                          placement="bottomLeft"
                                          overlayClassName="gender-popover"
                                        >
                                          <Input
                                            value={formatDisplayValue(editValue, attribute.attribute_type, attribute.name)}
                                            readOnly
                                            style={styles.inputField}
                                            placeholder="Select gender"
                                            onClick={() => {
                                              setPopoverVisible(prev => ({ ...prev, gender: true }));
                                            }}
                                          />
                                        </Popover>
                                      ) : (attribute.name === 'country' || attribute.name === 'province' || attribute.name === 'city') ? (
                                        <Popover
                                          content={
                                            attribute.name === 'country' ? (
                                              <Address
                                                countryListss={countryList}
                                                selectedLocation={{
                                                  country: selectedCountry,
                                                  state: selectedState,
                                                  city: selectedCity
                                                }}
                                                handleLocationClick={(type, name, id) => {
                                                  handleLocationSelect(type, name, id);
                                                }}
                                                locationType="primary"
                                              />
                                            ) : attribute.name === 'province' ? (
                                              <Province
                                                stateList={stateList}
                                                selectedLocation={{
                                                  country: selectedCountry,
                                                  state: selectedState,
                                                  city: selectedCity
                                                }}
                                                handleLocationClick={(type, name, id) => {
                                                  handleLocationSelect(type, name, id);
                                                }}
                                                locationType="primary"
                                              />
                                            ) : (
                                              <City
                                                cityList={cityList}
                                                selectedLocation={{
                                                  country: selectedCountry,
                                                  state: selectedState,
                                                  city: selectedCity
                                                }}
                                                handleLocationClick={(type, name, id) => {
                                                  handleLocationSelect(type, name, id);
                                                }}
                                                locationType="primary"
                                              />
                                            )
                                          }
                                          trigger="click"
                                          placement="bottomLeft"
                                          visible={popoverVisible[attribute.name]}
                                          onVisibleChange={(visible) => {
                                            setPopoverVisible({
                                              ...popoverVisible,
                                              [attribute.name]: visible
                                            });
                                          }}
                                          overlayClassName="location-popover"
                                        >
                                          <Input
                                            value={editValue}
                                            readOnly
                                            style={styles.inputField}
                                            placeholder={`Select ${attribute.label.toLowerCase()}`}
                                            onClick={async () => {
                                              // Preload states if country is selected but states aren't loaded yet
                                              if (attribute.name === 'province') {
                                                if (selectedCountry.id) {
                                                  if (stateList.length === 0) {
                                                    await fetchStates(selectedCountry.id);
                                                  }
                                                } else {
                                                  // If no country is selected, try to find the country from the attribute value
                                                  const countryMatch = countryList.find(c => c.name === attribute.value);
                                                  if (countryMatch) {
                                                    setSelectedCountry({ name: countryMatch.name, id: countryMatch.id });
                                                    await fetchStates(countryMatch.id);
                                                  }
                                                }
                                              }
                                              // Preload cities if state is selected but cities aren't loaded yet
                                              else if (attribute.name === 'city') {
                                                if (selectedState.id) {
                                                  if (cityList.length === 0) {
                                                    await fetchCities(selectedState.id);
                                                  }
                                                } else {
                                                  // If no state is selected, try to find the state from the attribute value
                                                  const stateMatch = stateList.find(s => s.name === attribute.value);
                                                  if (stateMatch) {
                                                    setSelectedState({ name: stateMatch.name, id: stateMatch.id });
                                                    await fetchCities(stateMatch.id);
                                                  } else if (selectedCountry.id) {
                                                    // If we have a country but no state match, try to find the state in the full list
                                                    const states = await stateListApi(selectedCountry.id);
                                                    if (states?.data?.data) {
                                                      setStateList(states.data.data);
                                                      const foundState = states.data.data.find(s => s.name === attribute.value);
                                                      if (foundState) {
                                                        setSelectedState({ name: foundState.name, id: foundState.id });
                                                        await fetchCities(foundState.id);
                                                      }
                                                    }
                                                  }
                                                }
                                              }
                                            }}
                                          />
                                        </Popover>
                                      ) : (
                                        <Input
                                          type={getInputType(attribute.attribute_type)}
                                          value={editValue}
                                          onChange={handleInputChange}
                                          style={styles.inputField}
                                          placeholder={`Enter ${attribute.label.toLowerCase()}`}
                                          disabled={loading}
                                          min={attribute.attribute_type === 'number' ? '0' : undefined}
                                          step={attribute.attribute_type === 'number' ? '1' : undefined}
                                        />
                                      )}
                                      {validationError && editingId === attribute.id && (
                                        <Text type="danger" style={{ fontSize: '12px' }}>{validationError}</Text>
                                      )}
                                    </div>
                                    <Space className='items-center'>
                                      <Button 
                                        type="text" 
                                        icon={<Tick width="1vw" height="1vw" strokeWidth="3.2" />}
                                        onClick={() => handleSave(scope.id, attribute)}
                                        loading={loading}
                                        disabled={loading}
                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                      />
                                      <Button 
                                        type="text" 
                                        icon={<Close stroke="#616874" width="2vw" height="2vw" circle={false} />}
                                        onClick={handleCancelEdit}
                                        disabled={loading}
                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                      />
                                    </Space>
                                  </>
                                ) : (
                                  <>
                                    <div style={styles.inputField}>
                                      {formatDisplayValue(attribute.value, attribute.attribute_type, attribute.name)}
                                    </div>
                                    {!restrictedAttributes.includes(attribute.name) && (
                                      <>
                                        <Button 
                                          type="text" 
                                          icon={<Edit stroke="#616874" style={{ width: '16px', height: '16px' }} />}
                                          onClick={() => handleEditClick(attribute, attribute.value)}
                                          disabled={loading}
                                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        />
                                        {attribute.value && (
                                          <Button 
                                            type="text" 
                                            danger
                                            icon={<CustomDelete color="#F96056" width="1vw" height="1vw" />}
                                            onClick={() => {
                                              const value = attributes.contact_person_values.find(
                                                v => v.contact_person_profile_attribute_id === attribute.id
                                              );
                                              if (value?.id) {
                                                handleDelete(value.id);
                                              }
                                            }}
                                            loading={loading}
                                            disabled={loading}
                                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                          />
                                        )}
                                      </>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                        {/* Add Attribute Button and Form */}
                        {activeAddAttributeScope === scope.id ? (
                          <div className="mt-4">
                            <div className="flex items-center gap-4">
                              {/* Attribute name */}
                              <div className="flex-column w-[45%]">
                                <label className="block text-[.8vw] font-medium text-gray-700 mb-1 pl-1 w-[60%]">Attribute Name</label>
                                <Input
                                  className="w-[75%] text-[0.8vw] rounded-lg"
                                  value={newAttribute.name}
                                  onChange={(e) => setNewAttribute({ ...newAttribute, name: e.target.value })}
                                  placeholder="Enter attribute name"
                                />
                              </div>
                              {/* Attribute type */}
                              <div className="w-[30%] pl-7">
                              <label className="block text-[.8vw] font-medium text-gray-700 mb-1 pl-1">Attribute Type</label>
                                <Dropdown
                                  open={newAttribute.typeDropdownOpen}
                                  setOpen={(isOpen) => {
                                    setNewAttribute({ ...newAttribute, typeDropdownOpen: isOpen });
                                  }}
                                  data={attributeTypes.map(type => ({ id: type.name, label: type.code }))}
                                  width="min-w-[100%]"
                                  height="max-h-[32vh]"
                                  className="w-[70%] text-[0.8vw] rounded-lg"
                                  editFilter={(selectedId) => {
                                    setNewAttribute(prev => ({
                                      ...prev,
                                      type: selectedId,
                                      typeDropdownOpen: false
                                    }));
                                  }}
                                  button={
                                    <div className="w-full">
                                      <div
                                        className="flex justify-between items-center py-1 px-2 cursor-pointer transition-colors duration-100 border border-gray-200 rounded-lg"
                                        onClick={() => {
                                          setNewAttribute({ ...newAttribute, typeDropdownOpen: !newAttribute.typeDropdownOpen });
                                        }}
                                      >
                                        <p className="flex items-center gap-1 text-gray-800">
                                          {newAttribute.type ? attributeTypes.find(t => t.name === newAttribute.type)?.code || 'Select type' : 'Select type'}
                                        </p>
                                        <span className={`duration-300 ${newAttribute.typeDropdownOpen ? '-rotate-180' : 'rotate-0'}`}>
                                          <div className="border-[0.4vw] rounded-t-[0.2vw] border-transparent border-t-[#777] translate-y-[25%]" />
                                        </span>
                                      </div>
                                    </div>
                                  }
                                />
                              </div>

                              {/* Create close and open buttons */}
                              <div className="flex items-center space-x-2 mt-6">
                                <button
                                  onClick={handleCreateAttribute}
                                  className="flex items-center justify-center w-6 h-6"
                                  disabled={!newAttribute.name || !newAttribute.type}
                                >
                                  <Tick width="0.8vw" height="0.8vw" strokeWidth="3.2" />
                                </button>
                                <button
                                  onClick={handleAddAttributeClose}
                                  className="flex items-center justify-center w-6 h-6 hover:bg-gray-100 rounded-full transition-colors"
                                >
                                  <Close
                                    stroke="#616874"
                                    circle={false}
                                    width="1.5vw"
                                    height="1.5vw"
                                  />
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div 
                            className="flex text-[#616874] text-sm items-center font-medium gap-2 cursor-pointer max-w-[fit-content] hover:bg-gray-100 mt-[20px]"
                            onClick={() => handleAddAttribute(scope.id)}
                          >
                            <AddCircle width="1rem" height="1rem" stroke="#616874" />
                            <span>Add Attribute</span>
                          </div>
                        )}
                      </div>
                    </Card>
                  ))
                ) : (
                  <div style={styles.emptyState}>
                    <Text style={{ fontSize: '1rem' }}>No profile attributes available for this contact</Text>
                  </div>
                )}
                
                {/* Add Scope Button */}
                <div className="mt-6">
                  {newScope.isAdding ? (
                    <div className="p-4 bg-white rounded-lg border border-gray-200">
                        <div className="flex items-center">
                          <div className="w-[40%]">
                            <label className="block text-[1vw] font-semibold text-gray-700 mb-1">Scope Name</label>
                            <Input
                              className='w-[100%] text-[0.8vw] rounded-lg'
                              value={newScope.name}
                              onChange={(e) => setNewScope({...newScope, name: e.target.value})}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveScope();
                                if (e.key === 'Escape') handleCancelScope();
                              }}
                              placeholder="Enter scope name"
                              autoFocus
                            />
                          </div>
                          <div className="flex items-center gap-2 ml-4 mt-[2vw]">
                            <button
                              onClick={handleSaveScope}
                              className="text-gray-500"
                              disabled={!newScope.name.trim()}
                            >
                              <Tick width="1vw" height="1vw" strokeWidth="3.2" />
                            </button>
                            <button
                              onClick={handleCancelScope}
                              className="text-gray-500"
                            >
                              <Close stroke='#616874' circle={false} width='2vw' height='2vw' />
                            </button>
                          </div>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="flex text-[black] text-[1vw] items-center font-semibold gap-[.4vw] cursor-pointer w-20 pt-2 ml-4"
                      onClick={handleAddScope}
                    >
                      <AddCircle width="1rem" height="1rem" stroke="black" />
                      <span>Add Scope</span>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactPersonSettings;
